import { Country } from 'react-phone-number-input'

import { useAppSelector } from 'store'
import { ExtendedError, UserBranch } from 'store/types'
import branchQuery, { MasterBranchType } from 'queries/branch'
import { useLoginMaster } from 'queries/master'
import useAuthenticate from 'hooks/use-authenticate'

import { useGeoCode } from './use-geo-location'
import { AppQueryOptions } from './use-app-query'
import { MasterBranch } from 'services/branch'

type ResponseType = {
  userBranches: UserBranch[]
  selectedBranch: UserBranch | undefined
  countryCode: Country
  masterBranch?: MasterBranchType
  currency: string
  isLoading: boolean
  isError: boolean
  error?: ExtendedError | null
}

const useBranch = (options?: AppQueryOptions<MasterBranch>): ResponseType => {
  const {
    user: { selectedBranch, userBranches, user },
  } = useAppSelector((state) => state)
  const [master] = useLoginMaster()
  const { isAuthenticated, isSelectedBranch } = useAuthenticate()
  const { data: masterBranch, isLoading, isError, error } = branchQuery.useItem(
    {
      branch_id: selectedBranch?.branchId ?? '',
    },
    {
      enabled: isAuthenticated && isSelectedBranch,
      ...options,
    },
  )

  /**
   * Only fetch Google API geocoding when
   * - User is login
   * - No branch country code
   * - No master country code
   *
   * This is to reduce the Google API cost.
   * We might have issue when we have multiple branches different country in one schema,
   * but we are moving away from it, one database, one master schema, and country.
   */
  const { data: geoCode } = useGeoCode(!selectedBranch?.countryCode && !master?.country_code && !!user)
  const defaultCountryCode = selectedBranch?.countryCode || master?.country_code || geoCode?.country.code || ''

  return {
    selectedBranch,
    userBranches,
    countryCode: defaultCountryCode as Country,
    masterBranch,
    currency: masterBranch?.currency_iso ?? '-',
    isLoading,
    isError,
    error,
  }
}

export default useBranch
